/**
 * Performance Summary Cards
 * Three-card layout showing Portfolio, Benchmark, and Alpha
 * Universal language that serves RM, AM, and IA personas
 */

import React from 'react';
import { TrendingUp, TrendingDown, Target } from 'lucide-react';

interface PerformanceSummaryCardsProps {
  portfolioReturn: number | null;
  benchmarkReturn: number | null;
  alpha: number | null;
  period?: string; // e.g., "YTD", "1Y", "3Y"
  benchmarkName?: string; // e.g., "SPI Index", "MSCI World"
  // TWR tooltip data (Pretim v1.1)
  twrYtdFactor?: number;
  twrStartingBaseMv?: number;
  twrEndingBaseMv?: number;
}

const PerformanceSummaryCards: React.FC<PerformanceSummaryCardsProps> = ({
  portfolioReturn,
  benchmarkReturn,
  alpha,
  period = 'YTD',
  benchmarkName = 'SPI Index',
  twrYtdFactor,
  twrStartingBaseMv,
  twrEndingBaseMv
}) => {
  const formatPercent = (value: number | null) => {
    if (value == null) return 'N/A';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  // TASK-654: the comparison is spoken in plain language — "ahead of / behind
  // <benchmark> by N percentage points" — never a bare Greek label, and an
  // unavailable comparison is stated, not shown as zero.
  const alphaSentence =
    alpha == null
      ? 'Comparison unavailable — benchmark or portfolio return is missing'
      : alpha === 0
        ? `In line with ${benchmarkName} (${period})`
        : `${alpha > 0 ? 'Ahead of' : 'Behind'} ${benchmarkName} by ${Math.abs(alpha).toFixed(1)} percentage points (${period})`;

  const formatCHF = (value: number) =>
    new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF', maximumFractionDigits: 0 }).format(value);

  const twrTooltip = twrYtdFactor != null
    ? `TWR Factor: ${twrYtdFactor.toFixed(4)}${twrStartingBaseMv != null && twrEndingBaseMv != null ? ` | Starting: ${formatCHF(twrStartingBaseMv)} \u2192 Ending: ${formatCHF(twrEndingBaseMv)}` : ''}`
    : undefined;

  const getAlphaStatus = () => {
    if (alpha == null) return { icon: TrendingUp, color: 'text-gray-400', bg: 'bg-gray-50', border: 'border-gray-200' };
    if (alpha > 0) return { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' };
    if (alpha > -1) return { icon: TrendingDown, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' };
    return { icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
  };

  const alphaStatus = getAlphaStatus();
  const AlphaIcon = alphaStatus.icon;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Portfolio Return */}
      <div
        className="bg-white rounded-lg border-2 border-[#002B45] p-6 text-center"
        title={twrTooltip}
      >
        <div className="text-sm text-gray-600 uppercase tracking-wide mb-2">
          Portfolio
        </div>
        <div className={`text-4xl font-bold mb-1 ${
          portfolioReturn == null ? 'text-gray-400' : portfolioReturn >= 0 ? 'text-green-600' : 'text-red-600'
        }`}>
          {formatPercent(portfolioReturn)}
        </div>
        <div className="text-sm text-gray-500">
          {period} Return
        </div>
      </div>

      {/* Benchmark Return */}
      <div className="bg-white rounded-lg border border-gray-300 p-6 text-center">
        <div className="text-sm text-gray-600 uppercase tracking-wide mb-2">
          Benchmark
        </div>
        <div className="text-4xl font-bold text-gray-700 mb-1">
          {formatPercent(benchmarkReturn)}
        </div>
        <div className="text-sm text-gray-500 flex items-center justify-center gap-1">
          <Target className="w-3 h-3" />
          {benchmarkName}
        </div>
      </div>

      {/* Alpha (Performance vs Benchmark) */}
      <div className={`bg-white rounded-lg border-2 ${alphaStatus.border} ${alphaStatus.bg} p-6 text-center`}>
        <div className="text-sm text-gray-600 uppercase tracking-wide mb-2">
          Performance
        </div>
        <div className={`text-4xl font-bold mb-1 flex items-center justify-center gap-2 ${alphaStatus.color}`}>
          <AlphaIcon className="w-8 h-8" />
          {formatPercent(alpha)}
        </div>
        <div className="text-sm text-gray-500" data-testid="alpha-sentence">
          {alphaSentence}
        </div>
      </div>
    </div>
  );
};

export default PerformanceSummaryCards;
